<template>
    <AppSection
        v-if="prizesToShow"
        :backgroundColor="backgroundColor"
        :backgroundImage="backgroundImage"
        :topPadding="topPadding"
        :bottomPadding="bottomPadding"
        :fillHeight="fillHeight"
    >
        <div id="prize-list">
            <div class="scc-prize-list-alternate scc-container">
                <div v-if="title" class="scc-container__row">
                    <div class="scc-container__column scc-container__column--12">
                        <h2 class="scc-prize-list-alternate__title">{{ prizesToShow ? title : '' }}</h2>
                    </div>
                </div>
                <template v-if="prizesToShow">
                    <div class="scc-container__row">
                        <template v-for="(prize, index) in formattedPrizeList">
                            <div
                                v-if="!hideFiller && addFiller && index === 3"
                                :key="prize.id + index + '-filler'"
                                class="scc-container__column scc-container__lg-column--4"
                            >
                                <PrizeFillerAlternate v-bind="fillerOptions()" />
                            </div>
                            <div :key="prize.id + index" class="scc-container__column scc-container__lg-column--4">
                                <PrizeAlternate v-bind="prizeOptions(prize)" />
                            </div>
                        </template>
                    </div>
                    <div v-if="showButton" class="scc-container__row">
                        <div class="scc-container__column">
                            <button type="button" class="scc-prize-list-alternate__button" v-on:click="expandLimit">
                                {{ buttonText }}
                            </button>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </AppSection>
</template>

<script>
import './prize-list-alternate.scss';

import cloneDeep from 'lodash/cloneDeep';

import PrizeAlternate from '../drawings/PrizeAlternate';
import PrizeFillerAlternate from '../drawings/PrizeFillerAlternate';
import AppSection from '../global/AppSection';
import { ticketSaleOpen } from './../../utils/data-conversions';

export default {
    name: 'PrizeListAlternate',
    components: { PrizeAlternate, PrizeFillerAlternate, AppSection },
    props: {
        game: {
            type: Object,
            required: true,
        },
        drawings: {
            type: Array,
            required: true,
        },
        hasShadow: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
        },
        limit: {
            type: Number,
            default: 9,
        },
        buttonText: {
            type: String,
            default: 'Meer prijzen',
        },
        addFiller: {
            type: Boolean,
            default: false,
        },
        removeFillerAfterStop: {
            type: Boolean,
            default: true,
        },
        fillerIcon: {
            type: String,
        },
        fillerTitle: {
            type: String,
        },
        fillerButtonText: {
            type: String,
        },
        backgroundImage: {
            type: String,
        },
        backgroundColor: {
            type: String,
            required: false,
            default: 'white',
        },
        topPadding: {
            type: String,
            required: false,
            default: 'medium',
        },
        bottomPadding: {
            type: String,
            required: false,
            default: 'medium',
        },
        fillHeight: {
            type: Boolean,
            required: false,
            default: false,
        },
        fillerBackgroundColor: {
            type: String,
            required: false,
            default: 'primary',
            validator: function(val) {
                return ['primary', 'secondary', 'white'].includes(val);
            },
        },
    },
    data: () => ({
        showAll: false,
    }),
    __editor: {
        requiredData: ['game', 'drawings'],
        fields: {
            highlight: {
                widget: 'select',
                values: ['none', 'first', 'all'],
            },
            fillerBackgroundColor: {
                widget: 'select',
                values: ['primary', 'secondary', 'white'],
            },
            backgroundColor: {
                widget: 'select',
                values: ['white', 'black', 'grey', 'primary', 'secondary'],
            },
            topPadding: {
                widget: 'select',
                values: ['none', 'small', 'medium', 'large', 'x-large', 'xx-large'],
            },
            bottomPadding: {
                widget: 'select',
                values: ['none', 'small', 'medium', 'large', 'x-large', 'xx-large'],
            },
            fillHeight: {
                widget: 'checkbox',
            },
        },
    },
    computed: {
        hideFiller() {
            return this.removeFillerAfterStop && this.ticketSaleClosed;
        },
        ticketSaleClosed() {
            return !ticketSaleOpen(this.game);
        },
        prizesToShow() {
            return this.formattedPrizeList && this.formattedPrizeList.length > 0;
        },
        nextDrawing() {
            const allDrawings = cloneDeep(this.drawings);
            // Order drawing by closeAt param
            const sortedDrawings = allDrawings.sort((a, b) => {
                return new Date(a.closeAt) - new Date(b.closeAt);
            });
            // Return first drawing with open sales period
            const nextDrawing = sortedDrawings.find(drawing => {
                return new Date(drawing.closeAt) > new Date();
            });
            return nextDrawing || sortedDrawings[sortedDrawings.length - 1];
        },
        formattedPrizeList() {
            if (this.nextDrawing) {
                let limit = this.limit && this.limit > 0 && !this.showAll ? this.limit : 99999999;
                return this.nextDrawing.prizes.slice(0, limit);
            } else return [];
        },
        showButton() {
            let limit = this.limit && this.limit > 0 ? this.limit : 99999999;
            return this.nextDrawing.prizes.length > limit && !this.showAll;
        },
    },
    methods: {
        expandLimit() {
            this.showAll = true;
        },
        prizeOptions(prize) {
            const { id, image, quantity, reference, description, sponsor, highlight, unitValue } = prize;

            return {
                id,
                image,
                quantity,
                reference,
                description,
                sponsor,
                highlight,
                unitValue,
                hasShadow: this.hasShadow,
            };
        },
        fillerOptions() {
            return {
                icon: this.fillerIcon,
                title: this.fillerTitle,
                buttonText: this.fillerButtonText,
                backgroundColor: this.fillerBackgroundColor,
                fillHeight: true,
                hasShadow: this.hasShadow,
            };
        },
    },
};
</script>
